import { useState } from 'react';
import { Icon } from '../components/chrome/Icon';
import { useEncryption } from './EncryptionProvider';
import { useT } from '../i18n';

/**
 * Settings card showing the media-encryption status and — when E2EE is on —
 * the password stored on this device, with a reminder to keep it safe.
 */
export function EncryptionSettingsCard() {
  const t = useT();
  const enc = useEncryption();
  const [show, setShow] = useState(false);

  return (
    <section>
      <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">{t('encryption.settings.title', 'Photo privacy')}</h2>
      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        {/* Status row */}
        <div className="flex items-center justify-between px-4 py-3.5">
          <span className="text-[14.5px] text-ink">{t('encryption.settings.rowLabel', 'End-to-end encryption')}</span>
          <span className="flex items-center gap-1.5 text-[13px] font-semibold">
            {enc.isEncryptedMode ? (
              <span className="inline-flex items-center gap-1 text-accent-ink">
                <Icon name="check" size={14} color="var(--color-accent)" /> {t('common.on', 'On')}
              </span>
            ) : enc.mode === 'none' ? (
              <span className="text-ink-3">{t('common.off', 'Off')}</span>
            ) : (
              <span className="text-ink-3">{t('encryption.settings.notSet', 'Not set')}</span>
            )}
          </span>
        </div>

        {enc.isEncryptedMode && (
          <div className="border-t border-hairline px-4 py-3.5">
            {enc.status === 'ready' && enc.password ? (
              <>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                  {t('encryption.settings.passwordLabel', 'Your photo password')}
                </p>
                <div className="flex min-h-11 items-center gap-2 rounded-xl border border-line bg-bg px-3.5">
                  <span className="min-w-0 flex-1 truncate font-mono text-[14px] text-ink">
                    {show ? enc.password : '•'.repeat(Math.min(enc.password.length, 16))}
                  </span>
                  <button onClick={() => setShow((s) => !s)} className="shrink-0 text-[12px] font-semibold text-ink-3">
                    {show ? t('common.hide', 'Hide') : t('common.show', 'Show')}
                  </button>
                </div>
                {enc.hint && <p className="mt-1.5 text-[11.5px] text-ink-3">{t('encryption.hintPrefix', 'Hint:')} {enc.hint}</p>}
                <div
                  className="mt-2.5 rounded-[10px] px-3 py-2.5 text-[11.5px] leading-snug"
                  style={{ background: 'rgba(181, 82, 59, 0.10)', border: '1px solid rgba(181, 82, 59, 0.22)', color: '#7E3320' }}
                >
                  {t('encryption.settings.keepSafeWarning', "Keep this password somewhere safe. We can't reset it — if it's lost, your encrypted photos can't be recovered.")}
                </div>
              </>
            ) : (
              <button
                onClick={() => void enc.ensureReady()}
                className="flex min-h-11 w-full items-center justify-center rounded-3xl border border-line text-[14px] font-semibold text-ink"
              >
                {t('encryption.settings.unlockCta', 'Enter password to unlock on this device')}
              </button>
            )}
          </div>
        )}

        {enc.mode === 'none' && (
          <div className="border-t border-hairline px-4 py-3 text-[12.5px] leading-snug text-ink-3">
            {t('encryption.settings.noneNote', 'Photos are stored without encryption. Your choice applies to new uploads.')}
          </div>
        )}
      </div>
    </section>
  );
}
